import React from 'react';
import { ShowcaseSection, ShowcaseItem } from '../ShowcaseItem';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/custom/empty-state/empty-state';
import { Loader, LoaderContainer, LoaderSpinner } from '@/components/custom/loader/loader';
import { Disclaimer, DisclaimerTitle, DisclaimerIcon, DisclaimerContent, DisclaimerText } from '@/components/custom/disclaimer/disclaimer';
import { BannerInfo, BannerInfoContent, BannerInfoIcon, BannerInfoTitle, BannerInfoDescription } from '@/components/custom/banner/banner-info';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { FiAlertTriangle, FiClipboard, FiInfo } from 'react-icons/fi';

const EMPTY_TYPES = ['register', 'content', 'notification', 'date', 'select', 'add', 'data', 'project', 'processed', 'discipline', 'disapproved', 'selectCard'];

export function FeedbackSection() {
  return (
    <ShowcaseSection
      id="feedback"
      title="Feedback & States"
      description="Alerts, empty states, loaders, disclaimers, banners, skeletons, popover"
    >
      <ShowcaseItem
        label="Alert"
        description="4 variantes (default, plain, success, danger)"
      >
        <div className="space-y-3">
          <Alert>
            <AlertTitle>Default</AlertTitle>
            <AlertDescription>Informacao importante para o usuario.</AlertDescription>
          </Alert>
          <Alert variant="success">
            <AlertTitle>Sucesso</AlertTitle>
            <AlertDescription>Acao realizada com sucesso!</AlertDescription>
          </Alert>
          <Alert variant="danger">
            <AlertTitle>Erro</AlertTitle>
            <AlertDescription>Ocorreu um erro ao processar.</AlertDescription>
          </Alert>
          <Alert variant="plain">
            <AlertTitle>Plain</AlertTitle>
            <AlertDescription>Sem fundo, apenas conteudo.</AlertDescription>
          </Alert>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="EmptyState — com CTA"
        description="Estado vazio com ilustracao + titulo + texto + botao de acao (padrao do app)"
      >
        <EmptyState
          type="add"
          title="Nenhuma turma cadastrada"
          text="Crie a primeira turma para comecar."
          cta={{ text: 'Criar turma', onClick: () => {} }}
        />
      </ShowcaseItem>

      <ShowcaseItem
        label="EmptyState — variante plain"
        description="Sem fundo de cartao (apenas ilustracao + texto)"
      >
        <EmptyState
          type="data"
          variant="plain"
          title="Sem dados ainda"
          text="Nenhuma versao foi enviada ainda."
        />
      </ShowcaseItem>

      <ShowcaseItem
        label="EmptyState — Galeria de tipos"
        description="12 tipos de ilustracao disponiveis"
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {EMPTY_TYPES.map(type => (
            <EmptyState
              key={type}
              type={type}
              title={type}
              text={`Nada para mostrar em "${type}".`}
            />
          ))}
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="Skeleton"
        description="Placeholder animado para loading state"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
          <Skeleton className="h-32 w-full rounded-lg" />
          <div className="grid grid-cols-3 gap-3">
            <Skeleton className="h-20 rounded-lg" />
            <Skeleton className="h-20 rounded-lg" />
            <Skeleton className="h-20 rounded-lg" />
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="Loader"
        description="Barra de progresso animada (uso tipico em listas)"
      >
        <Loader />
      </ShowcaseItem>

      <ShowcaseItem
        label="LoaderContainer"
        description="Container com altura minima e centralizacao"
      >
        <LoaderContainer>
          <span className="text-sm text-neutral-dark">Carregando dados...</span>
        </LoaderContainer>
      </ShowcaseItem>

      <ShowcaseItem
        label="LoaderSpinner"
        description="Spinner circular com texto opcional"
      >
        <LoaderSpinner text="Gerando relatorio" />
      </ShowcaseItem>

      <ShowcaseItem
        label="Disclaimer (clicavel)"
        description="Aviso destacado com borda colorida — uso real: alerta clicavel do dashboard"
      >
        <div className="space-y-3">
          <Disclaimer>
            <DisclaimerIcon>
              <FiClipboard />
            </DisclaimerIcon>
            <DisclaimerContent>
              <DisclaimerTitle>Atividade pendente</DisclaimerTitle>
              <DisclaimerText>3 atividades pendentes que vencem hoje.</DisclaimerText>
            </DisclaimerContent>
          </Disclaimer>
          <Disclaimer>
            <DisclaimerIcon>
              <FiAlertTriangle />
            </DisclaimerIcon>
            <DisclaimerContent>
              <DisclaimerTitle>Prazo vencendo</DisclaimerTitle>
              <DisclaimerText>Sua redacao precisa ser enviada ate amanha.</DisclaimerText>
            </DisclaimerContent>
          </Disclaimer>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="BannerInfo"
        description="Banner informativo com icone, titulo e descricao"
      >
        <BannerInfo>
          <BannerInfoContent>
            <BannerInfoIcon>
              <FiInfo size={14} />
            </BannerInfoIcon>
            <BannerInfoTitle>Nova funcionalidade disponivel</BannerInfoTitle>
            <BannerInfoDescription>O sistema agora suporta integracao com seu calendario externo.</BannerInfoDescription>
          </BannerInfoContent>
        </BannerInfo>
      </ShowcaseItem>

      <ShowcaseItem
        label="Popover"
        description="Conteudo flutuante ancorado a um trigger (Radix)"
      >
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline">Abrir popover</Button>
          </PopoverTrigger>
          <PopoverContent>
            <h4 className="mb-2 text-sm font-bold text-primary-dark">Informacao adicional</h4>
            <p className="m-0 text-xs text-neutral-dark">Use Popover para mostrar conteudo contextual sem abrir um modal completo. Ideal para tooltips ricos, opcoes inline ou previews.</p>
          </PopoverContent>
        </Popover>
      </ShowcaseItem>
    </ShowcaseSection>
  );
}
